import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Spinner, Wallet, CheckCircle, XCircle, Clock, CurrencyCircleDollar, User, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CashHandoverPage = () => {
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const [handovers, setHandovers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoPage, setHoPage] = useState(1);
  const [hoPageSize, setHoPageSize] = useState(20);
  const [actionLoading, setActionLoading] = useState({});
  const [detailDialog, setDetailDialog] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [hRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/cash-handovers`),
        axios.get(`${API_URL}/api/cash-summary`),
      ]);
      setHandovers(hRes.data);
      setSummary(sRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateHandover = async () => {
    setActionLoading(p => ({ ...p, create: true }));
    try {
      await axios.post(`${API_URL}/api/cash-handovers`);
      toast.success("Kasa teslim talebi olusturuldu");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata olustu");
    } finally {
      setActionLoading(p => ({ ...p, create: false }));
    }
  };

  const handleConfirm = async (id) => {
    setActionLoading(p => ({ ...p, [id]: true }));
    try {
      await axios.put(`${API_URL}/api/cash-handovers/${id}/confirm`);
      toast.success("Kasa teslimi onaylandi");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata olustu");
    } finally {
      setActionLoading(p => ({ ...p, [id]: false }));
    }
  };

  const handleReject = async (id) => {
    setActionLoading(p => ({ ...p, [id]: true }));
    try {
      await axios.put(`${API_URL}/api/cash-handovers/${id}/reject`);
      toast.success("Kasa teslimi reddedildi");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata olustu");
    } finally {
      setActionLoading(p => ({ ...p, [id]: false }));
    }
  };

  const statusBadge = (s) => {
    if (s === "confirmed") return <Badge className="bg-emerald-100 text-emerald-700" data-testid="status-confirmed">Onaylandi</Badge>;
    if (s === "rejected") return <Badge className="bg-red-100 text-red-700" data-testid="status-rejected">Reddedildi</Badge>;
    return <Badge className="bg-amber-100 text-amber-700" data-testid="status-pending">Bekliyor</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="cash-handover-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Nakit Kasa Yonetimi</h1>
          <p className="text-slate-500 mt-1">Nakit tahsilat ve kasa teslim islemleri</p>
          <span className="inline-flex items-center text-xs text-slate-400 mt-1" data-testid="retention-info">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            Son 180 gün gösteriliyor
          </span>
        </div>
        {isStaff && (
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleCreateHandover}
            disabled={actionLoading.create || (summary?.total_pending || 0) === 0}
            data-testid="create-handover-btn">
            {actionLoading.create ? <Spinner size={16} className="animate-spin mr-2" /> : <Wallet size={16} className="mr-2" />}
            Kasa Teslim Et
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 shadow-sm" data-testid="total-collected-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Toplam Nakit Tahsilat</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {"\u20BA"}{(summary?.total_cash_collected || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center">
                <CurrencyCircleDollar size={28} weight="duotone" className="text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="handed-over-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Teslim Edilen</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">
                  {"\u20BA"}{(summary?.total_handed_over || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center">
                <CheckCircle size={28} weight="duotone" className="text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="pending-cash-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Teslim Bekleyen</p>
                <p className="text-3xl font-bold text-amber-600 mt-1">
                  {"\u20BA"}{(summary?.total_pending || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center">
                <Clock size={28} weight="duotone" className="text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Summary */}
      {summary?.by_staff?.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <User size={18} className="text-indigo-600" /> Personel Bazli Nakit Ozet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Personel</TableHead>
                  <TableHead className="text-center">Tahsilat</TableHead>
                  <TableHead className="text-right">Toplam</TableHead>
                  <TableHead className="text-right">Teslim Edilen</TableHead>
                  <TableHead className="text-right">Bekleyen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.by_staff.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{s.staff_name}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="text-xs">{s.count}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{"\u20BA"}{s.total_collected.toFixed(2)}</TableCell>
                    <TableCell className="text-right text-emerald-600 font-medium">{"\u20BA"}{s.handed_over.toFixed(2)}</TableCell>
                    <TableCell className="text-right text-amber-600 font-bold">{"\u20BA"}{s.pending.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Handover History */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">Kasa Teslim Gecmisi</CardTitle>
        </CardHeader>
        <CardContent>
          {handovers.length === 0 ? (
            <div className="text-center py-12">
              <Wallet size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Henuz kasa teslim kaydi yok</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Personel</TableHead>
                  <TableHead className="text-center">Tahsilat</TableHead>
                  <TableHead className="text-right">Tutar</TableHead>
                  <TableHead className="text-center">Durum</TableHead>
                  <TableHead className="text-right">Islem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {handovers.slice((hoPage - 1) * hoPageSize, hoPage * hoPageSize).map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="text-sm">{new Date(h.created_at).toLocaleString("tr-TR")}</TableCell>
                    <TableCell className="font-medium">{h.staff_name}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="text-xs">{h.item_count}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-bold">{"\u20BA"}{h.total_amount.toFixed(2)}</TableCell>
                    <TableCell className="text-center">{statusBadge(h.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                          onClick={() => setDetailDialog(h)} data-testid={`detail-handover-${h.id}`}>
                          <ArrowRight size={12} className="mr-1" /> Detay
                        </Button>
                        {!isStaff && h.status === "pending" && (
                          <>
                            <Button size="sm" className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleConfirm(h.id)} disabled={actionLoading[h.id]}
                              data-testid={`confirm-handover-${h.id}`}>
                              {actionLoading[h.id] ? <Spinner size={12} className="animate-spin" /> : <CheckCircle size={12} className="mr-1" />}
                              Onayla
                            </Button>
                            <Button size="sm" variant="destructive" className="h-7 px-2 text-xs"
                              onClick={() => handleReject(h.id)} disabled={actionLoading[h.id]}
                              data-testid={`reject-handover-${h.id}`}>
                              <XCircle size={12} className="mr-1" /> Reddet
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>
      {handovers.length > hoPageSize && (
        <Pagination total={handovers.length} page={hoPage} pageSize={hoPageSize}
          onPageChange={setHoPage} onPageSizeChange={setHoPageSize} />
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Kasa Teslim Detayi</DialogTitle>
            <DialogDescription>
              {detailDialog?.staff_name} - {new Date(detailDialog?.created_at || "").toLocaleString("tr-TR")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-500">Toplam Tutar</span>
              <span className="text-xl font-bold text-slate-900">{"\u20BA"}{(detailDialog?.total_amount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-500">Durum</span>
              {detailDialog && statusBadge(detailDialog.status)}
            </div>
            {detailDialog?.confirmed_at && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Islem Tarihi</span>
                <span className="text-sm">{new Date(detailDialog.confirmed_at).toLocaleString("tr-TR")}</span>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Tahsilat Detaylari ({detailDialog?.items?.length || 0})</p>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {(detailDialog?.items || []).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-slate-900">{item.tenant_name}</p>
                      <p className="text-xs text-slate-500">{item.apartment_name} - {item.unit_name}</p>
                    </div>
                    <span className="font-bold text-slate-900">{"\u20BA"}{item.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialog(null)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashHandoverPage;
